/* =========================================================================
   CriteriaJson grammar evaluator (client side, PURE). ONE canonical evaluator
   for the achievement/quest/collectible/pilgrimage rule grammar seeded in the
   DB (db/001:681, architecture §0):

     {"trigger":"<EventKey>","conditions":[{"metric":"<name>","operator":"==|>=|<=|>|<","value":<literal>}]}

   NOTE for the record: the brief asked to "reuse quests.ts's evaluator", but
   quests.ts does NOT contain a grammar evaluator — it maps events to quest keys
   with a hardcoded switch (questForEvent) and a numeric progress reducer. There
   was no CriteriaJson evaluator anywhere in the client, so this module is the
   FIRST and now the single one; the Pilgrimage gates evaluate through it. It is
   deliberately dependency-free and unit-tested so quests/collections could adopt
   it later with zero rework.

   A gate is a themed COUNTER: every event that (a) matches the trigger and (b)
   satisfies the non-"Count" FILTER conditions advances the gate by one; the gate
   is satisfied once that count reaches the "Count" condition's value (default 1
   when no Count condition is present, e.g. a one-shot GalleryPurchased gate).
   ========================================================================= */

export interface Condition {
  metric: string;
  operator: string;
  value: unknown;
}
export interface Criteria {
  trigger: string;
  conditions: Condition[];
}

/** Parse a GateCriteriaJson string; returns null on malformed / empty input. */
export function parseCriteria(json: string | null | undefined): Criteria | null {
  if (!json) return null;
  try {
    const o = JSON.parse(json) as Partial<Criteria>;
    if (!o || typeof o.trigger !== 'string') return null;
    const conditions = Array.isArray(o.conditions) ? (o.conditions as Condition[]) : [];
    return { trigger: o.trigger, conditions };
  } catch {
    return null;
  }
}

/** Numeric comparison for the five supported operators. */
export function compareOp(a: number, op: string, b: number): boolean {
  switch (op) {
    case '==':
      return a === b;
    case '>=':
      return a >= b;
    case '<=':
      return a <= b;
    case '>':
      return a > b;
    case '<':
      return a < b;
    default:
      return false;
  }
}

/** Map a grammar metric name to the value carried in a telemetry event's props.
    Handles the grammar's PascalCase metric names against the client's camelCase
    props, plus the couple of known aliases (ItemKey→key, GameKey→game). */
export function metricValue(metric: string, props?: Record<string, unknown>): unknown {
  if (!props) return undefined;
  switch (metric) {
    case 'Category':
      return props.category;
    case 'ItemKey':
      return props.key ?? props.itemKey;
    case 'GameKey':
      return props.game ?? props.gameKey;
    default: {
      const camel = metric.charAt(0).toLowerCase() + metric.slice(1);
      return props[camel] ?? props[metric];
    }
  }
}

/** Does a single FILTER condition (metric !== 'Count') hold for these props?
    String '==' compares stringified values; the ordered operators compare
    numerically. */
export function conditionMatches(cond: Condition, props?: Record<string, unknown>): boolean {
  const actual = metricValue(cond.metric, props);
  if (cond.operator === '==') return String(actual) === String(cond.value);
  const a = Number(actual);
  const b = Number(cond.value);
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  return compareOp(a, cond.operator, b);
}

/** True when an event advances a gate: its key equals the trigger AND every
    non-Count filter condition holds. (Count is a threshold on the accumulated
    match count, evaluated separately by gateSatisfied.) */
export function eventMatchesCriteria(
  c: Criteria,
  eventKey: string,
  props?: Record<string, unknown>,
): boolean {
  if (c.trigger !== eventKey) return false;
  return c.conditions.filter((cond) => cond.metric !== 'Count').every((cond) => conditionMatches(cond, props));
}

/** The count threshold a gate must reach (the "Count" condition's value, or 1
    for one-shot gates with no Count condition). */
export function countTarget(c: Criteria): number {
  const count = c.conditions.find((cond) => cond.metric === 'Count');
  const n = count ? Number(count.value) : 1;
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/** Is the gate satisfied given how many matching events have accumulated? */
export function gateSatisfied(c: Criteria, matchCount: number): boolean {
  return matchCount >= countTarget(c);
}
